import type { Problem } from '@streepaf/shared'
import { clientId } from '../utils/ids.js'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem | null,
    readonly retryAfter?: number,
  ) {
    super(problem?.detail ?? problem?.title ?? `Request failed (${status})`)
    this.name = 'ApiError'
  }

  get isMissing(): boolean {
    return this.status === 404
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isRateLimited(): boolean {
    return this.status === 429
  }

  /** Retrying will not help: the server rejected the request itself. */
  get isPermanent(): boolean {
    return this.status >= 400 && this.status < 500 && this.status !== 429
  }
}

export interface RequestOptions {
  method?: string
  body?: unknown
  idempotencyKey?: string
  signal?: AbortSignal
  headers?: Record<string, string>
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    accept: 'application/json',
    'x-client-id': clientId(),
    ...options.headers,
  }
  if (options.body !== undefined) headers['content-type'] = 'application/json'
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey

  const response = await fetch(`/api${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (!response.ok) {
    let problem: Problem | null = null
    try {
      problem = (await response.json()) as Problem
    } catch {
      problem = null
    }
    const retryAfter = Number(response.headers.get('retry-after'))
    throw new ApiError(
      response.status,
      problem,
      Number.isFinite(retryAfter) ? retryAfter : undefined,
    )
  }

  if (response.status === 204) return undefined as T
  const type = response.headers.get('content-type') ?? ''
  if (!type.includes('application/json')) return (await response.text()) as T
  return (await response.json()) as T
}
